import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Users, Gauge, Fuel, Briefcase, Settings, Cog, Loader2, ArrowLeft } from "lucide-react";

const today = () => new Date().toISOString().split("T")[0];
const tomorrow = () => new Date(Date.now() + 86400000).toISOString().split("T")[0];

export default function CarDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [car, setCar] = useState(null);
  const [pickup, setPickup] = useState(today());
  const [ret, setRet] = useState(tomorrow());
  const [location, setLocation] = useState("");
  const [insurance, setInsurance] = useState(false);
  const [quote, setQuote] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get(`/cars/${id}`).then((res) => setCar(res.data)).catch(() => navigate("/fleet"));
  }, [id, navigate]);

  const fetchQuote = useCallback(async () => {
    if (!car || !pickup || !ret || ret <= pickup) {
      setQuote(null);
      return;
    }
    try {
      const { data } = await api.post("/bookings/quote", {
        car_id: car.id,
        pickup_date: pickup,
        return_date: ret,
        pickup_location: location || "TBD",
        insurance,
      });
      setQuote(data);
    } catch {
      setQuote(null);
    }
  }, [car, pickup, ret, location, insurance]);

  useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  const handleReserve = async () => {
    if (!user) {
      toast.info("Please sign in to complete your booking");
      navigate("/login", { state: { from: `/cars/${id}` } });
      return;
    }
    if (!location.trim()) {
      toast.error("Please enter a pickup location");
      return;
    }
    setPaying(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        car_id: car.id,
        pickup_date: pickup,
        return_date: ret,
        pickup_location: location,
        insurance,
      });
      const { data } = await api.post("/payments/checkout", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setPaying(false);
    }
  };

  if (!car)
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="car-loading">
        <Loader2 className="animate-spin text-emerald-600" />
      </div>
    );

  const specs = [
    { icon: <Users size={18} />, label: "Seats", value: car.seats },
    { icon: <Gauge size={18} />, label: "0-60 mph", value: car.zero_to_sixty },
    { icon: <Cog size={18} />, label: "Engine", value: car.engine },
    { icon: <Settings size={18} />, label: "Transmission", value: car.transmission },
    { icon: <Fuel size={18} />, label: "Fuel", value: car.fuel },
    { icon: <Briefcase size={18} />, label: "Luggage", value: car.luggage },
  ];

  return (
    <div className="App bg-slate-50 min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <button onClick={() => navigate("/fleet")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700 mb-6" data-testid="back-to-fleet">
          <ArrowLeft size={16} /> Back to fleet
        </button>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8">
          {/* Left: gallery + specs */}
          <div>
            <div className="rounded-3xl overflow-hidden bg-slate-100 border border-slate-200" data-testid="car-image">
              <img src={car.image_url} alt={car.name} className="w-full h-[380px] object-cover" />
            </div>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <Badge className="bg-emerald-100 text-emerald-700 border-0">{car.category}</Badge>
              <Badge className={car.available ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-100 text-amber-700 border-0"}>
                {car.available ? "Available now" : "Currently rented"}
              </Badge>
            </div>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold text-emerald-950" data-testid="car-name">{car.name}</h1>
            <p className="mt-3 text-slate-600 leading-relaxed max-w-2xl">{car.description}</p>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="specs-grid">
              {specs.map((s, i) => (
                <div key={i} className="bg-white rounded-2xl p-4 border border-slate-200/70">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">{s.icon}</span>
                  <p className="mt-3 text-xs text-slate-400 uppercase tracking-wide">{s.label}</p>
                  <p className="font-display font-bold text-emerald-950">{s.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: sticky booking card */}
          <div>
            <div className="sticky top-24 bg-white rounded-3xl border border-emerald-100 shadow-xl p-6" data-testid="booking-card">
              <div className="flex items-end justify-between">
                <div>
                  <span className="font-display font-extrabold text-4xl text-emerald-900">${car.daily_rate}</span>
                  <span className="text-slate-400"> / day</span>
                </div>
              </div>

              <div className="mt-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Pickup date</Label>
                    <Input type="date" min={today()} value={pickup} onChange={(e) => setPickup(e.target.value)} data-testid="pickup-date" className="mt-1 border-slate-200" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Return date</Label>
                    <Input type="date" min={pickup} value={ret} onChange={(e) => setRet(e.target.value)} data-testid="return-date" className="mt-1 border-slate-200" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Pickup location <span className="text-emerald-600 font-medium">(Boston area only)</span></Label>
                  <Input placeholder="e.g. Downtown Boston, Logan Airport, Mattapan" value={location} onChange={(e) => setLocation(e.target.value)} data-testid="pickup-location" className="mt-1 border-slate-200" />
                </div>
                <label className="flex items-center gap-3 bg-emerald-50/60 rounded-xl p-3 cursor-pointer" data-testid="insurance-toggle">
                  <Checkbox checked={insurance} onCheckedChange={(v) => setInsurance(!!v)} />
                  <span className="text-sm text-slate-700">Add full protection <span className="text-slate-400">($25/day)</span></span>
                </label>
              </div>

              {quote && (
                <div className="mt-5 border-t border-slate-100 pt-4 space-y-2 text-sm" data-testid="price-breakdown">
                  <Row label={`$${car.daily_rate} × ${quote.days} day(s)`} value={`$${(car.daily_rate * quote.days).toFixed(2)}`} />
                  {insurance && <Row label="Protection" value={`$${quote.insurance_total.toFixed(2)}`} />}
                  <Row label="Tax (8%)" value={`$${quote.tax.toFixed(2)}`} />
                  <div className="flex justify-between pt-2 border-t border-slate-100">
                    <span className="font-display font-bold text-emerald-950">Total</span>
                    <span className="font-display font-extrabold text-emerald-900 text-lg" data-testid="total-price">${quote.total.toFixed(2)}</span>
                  </div>
                </div>
              )}

              <Button
                onClick={handleReserve}
                disabled={paying || !car.available || !quote}
                data-testid="reserve-button"
                className="mt-5 w-full h-12 bg-emerald-900 hover:bg-emerald-800 rounded-xl text-base"
              >
                {paying ? <Loader2 className="animate-spin" /> : car.available ? "Reserve & Pay" : "Unavailable"}
              </Button>
              <p className="mt-3 text-center text-xs text-slate-400">Secure payment via Stripe · Free cancellation</p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between text-slate-600">
    <span>{label}</span>
    <span className="font-medium text-slate-800">{value}</span>
  </div>
);
